import { useEffect } from 'react';
import { useApolloClient } from '@apollo/client';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { sessionSubject } from '../state/sessionObservable';
import { UPDATE_CUSTOMER_DISPLAY } from '../graphql/customerDisplay';
import { getAppVersion } from '../utils/getAppVersion';
import { getPlatform } from '../utils/getPlatform';

export function useAppVersionSync(): void {
  const client = useApolloClient();

  useEffect(() => {
    const sub = sessionSubject
      .pipe(
        map(s => ({
          id: s.customerDisplay?.id,
          name: s.customerDisplay?.name,
          appVersion: s.customerDisplay?.appVersion,
          platform: s.customerDisplay?.platform,
        })),
        distinctUntilChanged(
          (a, b) =>
            a.id === b.id &&
            a.name === b.name &&
            a.appVersion === b.appVersion &&
            a.platform === b.platform,
        ),
      )
      .subscribe(async ({ id, name, appVersion, platform }) => {
        if (!id || !name) return;
        const currentVersion = getAppVersion();
        const currentPlatform = getPlatform();
        const isConcreteVersion = currentVersion !== 'CDS';
        if (
          platform === currentPlatform &&
          (!isConcreteVersion || appVersion === currentVersion)
        )
          return;
        try {
          await client.mutate({
            mutation: UPDATE_CUSTOMER_DISPLAY,
            variables: {
              input: {
                id,
                name,
                platform: currentPlatform,
                ...(isConcreteVersion && { appVersion: currentVersion }),
              },
            },
          });
        } catch (e) {
          console.warn('[useAppVersionSync] failed to sync app version', e);
        }
      });
    return () => sub.unsubscribe();
  }, [client]);
}
